/** Shared wallet balance polling after Stripe deposit confirmation / redirect return. */

export const WALLET_BALANCE_POLL_DEADLINE_MS = 25_000;
export const WALLET_BALANCE_POLL_PAUSE_MS = 350;

export type PollForCreditedBalanceOptions = {
  /**
   * Balance known to predate payment confirmation.
   * Must never be derived from `refresh()` after confirmation — that read may
   * already include the credit and would make `baseline + amountCents` unreachable.
   * Pass `null` when unknown; then `isCreditApplied` is required for early exit.
   */
  baselineCents: number | null;
  refresh: () => Promise<number | null>;
  /** When set with a known baseline, stop once balance reaches baseline + amountCents. */
  amountCents?: number;
  /**
   * Server-side credit check used when `baselineCents` is null (or as an additional
   * success path). Example: deposit continue returns 404/409 after webhook credit.
   */
  isCreditApplied?: () => Promise<boolean>;
  deadlineMs?: number;
  pauseMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
};

/**
 * Poll until the wallet balance reflects a credit.
 *
 * - Uses only a caller-supplied pre-confirmation baseline (never `refresh()` to seed it).
 * - With baseline + amountCents: stop when `balance >= baseline + amount`.
 * - With baseline only: stop when `balance > baseline`.
 * - With no baseline: stop when `isCreditApplied()` is true; otherwise refresh until deadline.
 */
export async function pollForCreditedBalance(
  opts: PollForCreditedBalanceOptions,
): Promise<void> {
  const now = opts.now ?? Date.now;
  const sleep = opts.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const deadlineMs = opts.deadlineMs ?? WALLET_BALANCE_POLL_DEADLINE_MS;
  const pauseMs = opts.pauseMs ?? WALLET_BALANCE_POLL_PAUSE_MS;

  const baseline = opts.baselineCents;
  const target =
    opts.amountCents != null && opts.amountCents > 0 && baseline != null
      ? baseline + opts.amountCents
      : null;

  const deadline = now() + deadlineMs;
  while (now() < deadline) {
    if (opts.isCreditApplied && (await opts.isCreditApplied())) {
      await opts.refresh();
      return;
    }

    const balance = await opts.refresh();
    if (balance != null) {
      if (target != null) {
        if (balance >= target) return;
      } else if (baseline != null) {
        if (balance > baseline) return;
      }
    }
    await sleep(pauseMs);
  }
  await opts.refresh();
}
